import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { setupApi } from '@/api';

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/setup',
      name: 'setup',
      component: () => import('@/views/SetupWizardView.vue'),
      meta: { public: true },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { public: true },
    },
    {
      path: '/',
      component: () => import('@/views/DashboardLayout.vue'),
      children: [
        { path: '', redirect: { name: 'overview' } },
        {
          path: 'overview',
          name: 'overview',
          component: () => import('@/views/OverviewView.vue'),
        },
        {
          path: 'services',
          name: 'services',
          component: () => import('@/views/ServicesView.vue'),
        },
        {
          path: 'services/:name',
          name: 'service-detail',
          component: () => import('@/views/ServiceDetailView.vue'),
          props: true,
        },
        {
          path: 'services/:name/console',
          name: 'service-console',
          component: () => import('@/views/ConsoleView.vue'),
          props: true,
        },
        {
          path: 'console',
          name: 'multi-console',
          component: () => import('@/views/MultiConsoleView.vue'),
        },
        {
          path: 'performance',
          name: 'performance',
          component: () => import('@/views/PerformanceView.vue'),
        },
        {
          path: 'backups',
          name: 'backups',
          component: () => import('@/views/BackupsView.vue'),
        },
        {
          path: 'groups',
          name: 'groups',
          component: () => import('@/views/GroupsView.vue'),
        },
        {
          path: 'templates',
          name: 'templates',
          component: () => import('@/views/TemplatesView.vue'),
        },
        {
          path: 'wrappers',
          name: 'wrappers',
          component: () => import('@/views/WrappersView.vue'),
        },
        {
          path: 'wrappers/add',
          name: 'wrapper-add',
          component: () => import('@/views/WrapperAddView.vue'),
        },
        {
          path: 'connections',
          name: 'connections',
          component: () => import('@/views/ConnectionsView.vue'),
        },
        {
          path: 'motd',
          name: 'motd',
          component: () => import('@/views/MotdView.vue'),
        },
        {
          path: 'maintenance',
          name: 'maintenance',
          component: () => import('@/views/MaintenanceView.vue'),
        },
        {
          // Schritt 10: Network-weiter max-Player-Count
          path: 'network-limits',
          name: 'network-limits',
          component: () => import('@/views/NetworkLimitsView.vue'),
        },
        {
          path: 'users',
          name: 'users',
          component: () => import('@/views/UsersView.vue'),
        },
        {
          path: 'account',
          name: 'account',
          component: () => import('@/views/AccountView.vue'),
        },
        {
          path: 'audit',
          name: 'audit',
          component: () => import('@/views/AuditView.vue'),
        },
      ],
    },
  ],
});

// Phase 23b: Setup-Guard. Beim ersten Master-Start (kein Setup-Marker)
// werden alle Routes auf /setup umgeleitet — der User KANN sich nicht
// einloggen bevor das Wizard durch ist. Cache ist tolerant gegen
// API-Errors (Master noch beim Booten, Network-Glitch etc.).
let setupCheckCache: { complete: boolean; checkedAt: number } | null = null;
const SETUP_CACHE_TTL = 60_000;

async function isSetupComplete(): Promise<boolean> {
  if (setupCheckCache && Date.now() - setupCheckCache.checkedAt < SETUP_CACHE_TTL) {
    return setupCheckCache.complete;
  }
  try {
    const state = await setupApi.state();
    setupCheckCache = { complete: state.setupComplete, checkedAt: Date.now() };
    return state.setupComplete;
  } catch {
    // API down? Lieber durchlassen statt blockieren — der Login-Flow wird's dann fangen
    return true;
  }
}

router.beforeEach(async (to) => {
  // Setup-Wizard ist immer erreichbar (auch wenn Setup schon durch — landest dann eh wieder auf Login)
  if (to.name === 'setup') return true;

  // Wenn Setup nicht durch ist: alle Routes redirecten
  if (!(await isSetupComplete())) {
    return { name: 'setup' };
  }

  const auth = useAuthStore();
  if (to.meta.public) return true;
  if (!auth.isAuthenticated) {
    return { name: 'login', query: { next: to.fullPath } };
  }
  return true;
});

export default router;